import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { cart, getTotalMRP, getTotalDiscountedPrice, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [customerState, setCustomerState] = useState('');
  const totalMRP = getTotalMRP();
  const totalDiscounted = getTotalDiscountedPrice();
  const hasSpecialPrices = cart.some(item => item.has_special_price);
  
  // Check if customer is from Delhi (By Hand - no dispatch options)
  const isDelhiCustomer = customerState?.toLowerCase() === 'delhi';
  
  const [formData, setFormData] = useState({
    company_name: '',
    gst_number: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    dispatch_mode: 'transport',  // 'transport', 'courier', or 'local'
    preferred_transporter: '',
    preferred_courier: '',  // New field for courier default
    private_marka: '',
    dispatch_remarks: '',
  });

  // Load saved customer info on mount
  useEffect(() => {
    const loadSavedInfo = async () => {
      if (user && token) {
        try {
          const response = await axios.get(`${API}/customers/checkout-info`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            setCustomerState(response.data.state || user.state || '');
            setFormData(prev => ({
              ...prev,
              company_name: response.data.company_name || '',
              gst_number: response.data.gst_number || '',
              customer_email: response.data.email || user.email || '',
              customer_phone: response.data.phone || user.mobile || '',
              shipping_address: response.data.shipping_address || '',
              preferred_transporter: response.data.preferred_transporter || '',
              preferred_courier: response.data.preferred_courier || '',
              private_marka: response.data.private_marka || '',
            }));
          }
        } catch (error) {
          // If no saved info, use user data
          setCustomerState(user.state || '');
          setFormData(prev => ({
            ...prev,
            customer_email: user.email || '',
            customer_phone: user.mobile || '',
          }));
        }
      }
    };
    loadSavedInfo();
  }, [user, token]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      await axios.post(`${API}/customers/checkout-info`, {
        company_name: formData.company_name,
        gst_number: formData.gst_number,
        email: formData.customer_email,
        phone: formData.customer_phone,
        shipping_address: formData.shipping_address,
        preferred_transporter: formData.preferred_transporter,
        private_marka: formData.private_marka,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Information saved for future orders!');
    } catch (error) {
      toast.error('Failed to save information');
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.company_name.trim()) {
      toast.error('Company Name is required');
      return;
    }
    if (!formData.customer_email.trim()) {
      toast.error('Email Address is required');
      return;
    }
    if (!formData.customer_phone.trim()) {
      toast.error('Phone Number is required');
      return;
    }
    if (!formData.shipping_address.trim()) {
      toast.error('Shipping Address is required');
      return;
    }
    
    // All customers must select dispatch mode and transporter/courier (except local dispatch)
    if (!formData.dispatch_mode) {
      toast.error('Please select a dispatch method');
      return;
    }
    // Transporter required for transport mode
    if (formData.dispatch_mode === 'transport' && !formData.preferred_transporter.trim()) {
      toast.error('Transporter name is required for Transport dispatch');
      return;
    }
    // Courier required for courier mode
    if (formData.dispatch_mode === 'courier' && !formData.preferred_courier.trim()) {
      toast.error('Courier name is required for Courier dispatch');
      return;
    }

    setLoading(true);

    try {
      // Use the correct transporter name based on dispatch mode
      const transporterName = formData.dispatch_mode === 'transport' ? formData.preferred_transporter :
                              formData.dispatch_mode === 'courier' ? formData.preferred_courier : '';
      
      const orderData = {
        company_name: formData.company_name,
        gst_number: formData.gst_number,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_category: user?.customer_category || 'RETAILER',
        shipping_address: formData.shipping_address,
        notes: formData.notes,
        dispatch_mode: formData.dispatch_mode,
        preferred_transporter: transporterName,
        private_marka: formData.private_marka,
        dispatch_remarks: formData.dispatch_remarks,
        items: cart.map((item) => ({
          jp_part_no: item.jp_part_no,
          quantity: item.quantity,
        })),
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-confirmation/${response.data.order_id}`);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#ffffff' }}>
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-5xl">
        <h1 className="text-3xl font-heading font-bold mb-8" data-testid="checkout-title">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="bg-white border border-border rounded-sm p-6">
              <h2 className="text-xl font-heading font-bold mb-6">Customer Information</h2>

              <div className="space-y-4">
                {/* Customer Category - Read Only */}
                <div>
                  <Label className="font-semibold text-gray-700">
                    Customer Category
                  </Label>
                  <div className="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-sm text-gray-700 font-medium">
                    {user?.customer_category || 'RETAILER'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Category is set by admin and cannot be changed</p>
                </div>

                {/* Company Name - Mandatory */}
                <div>
                  <Label htmlFor="company_name" className="font-semibold">
                    Company Name <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="Enter your company name"
                    required
                    data-testid="company-name-input"
                  />
                </div>

                {/* GST Number - Optional */}
                <div>
                  <Label htmlFor="gst_number" className="font-semibold">
                    GST Number <span className="text-gray-400">(Optional)</span>
                  </Label>
                  <Input
                    id="gst_number"
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => handleChange('gst_number', e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="e.g., 22AAAAA0000A1Z5"
                    data-testid="gst-number-input"
                  />
                </div>

                {/* Email - Mandatory */}
                <div>
                  <Label htmlFor="customer_email" className="font-semibold">
                    Email Address <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => handleChange('customer_email', e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="your@email.com"
                    required
                    data-testid="customer-email-input"
                  />
                </div>

                {/* Phone - Mandatory */}
                <div>
                  <Label htmlFor="customer_phone" className="font-semibold">
                    Phone Number <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => handleChange('customer_phone', e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="Enter phone number"
                    required
                    data-testid="customer-phone-input"
                  />
                </div>

                {/* Shipping Address - Mandatory */}
                <div>
                  <Label htmlFor="shipping_address" className="font-semibold">
                    Shipping Address <span className="text-red-600">*</span>
                  </Label>
                  <Textarea
                    id="shipping_address"
                    value={formData.shipping_address}
                    onChange={(e) => handleChange('shipping_address', e.target.value)}
                    className="rounded-sm mt-1"
                    rows={3}
                    placeholder="Enter complete shipping address"
                    required
                    data-testid="shipping-address-input"
                  />
                </div>

                {/* Notes - Optional */}
                <div>
                  <Label htmlFor="notes" className="font-semibold">
                    Order Notes <span className="text-gray-400">(Optional)</span>
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    className="rounded-sm mt-1"
                    rows={2}
                    placeholder="Any special instructions..."
                    data-testid="notes-input"
                  />
                </div>

                {/* Dispatch Section */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Dispatch Details</h3>
                  
                  {/* Delhi customer indicator */}
                  {isDelhiCustomer && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4">
                      <p className="text-sm text-blue-800 font-medium">
                        📍 Delhi Customer
                      </p>
                    </div>
                  )}
                  
                  {/* Dispatch Mode Selection - Available for ALL customers */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dispatch_mode" className="font-semibold">
                        Dispatch Method <span className="text-red-600">*</span>
                      </Label>
                      <Select
                        value={formData.dispatch_mode}
                        onValueChange={(value) => handleChange('dispatch_mode', value)}
                      >
                        <SelectTrigger className="rounded-sm mt-1" data-testid="dispatch-mode-select">
                          <SelectValue placeholder="Select dispatch method" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="transport">Transport</SelectItem>
                          <SelectItem value="courier">Courier</SelectItem>
                          <SelectItem value="local">Local Dispatch (Loose)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Transporter/Courier Name - Only required for transport/courier, not local */}
                    {formData.dispatch_mode === 'transport' && (
                      <div>
                        <Label htmlFor="preferred_transporter" className="font-semibold">
                          Transporter Name <span className="text-red-600">*</span>
                        </Label>
                        <Input
                          id="preferred_transporter"
                          type="text"
                          value={formData.preferred_transporter}
                          onChange={(e) => handleChange('preferred_transporter', e.target.value)}
                          className="rounded-sm mt-1"
                          placeholder="Your preferred transport company"
                          required
                          data-testid="transporter-input"
                        />
                      </div>
                    )}
                    
                    {formData.dispatch_mode === 'courier' && (
                      <div>
                        <Label htmlFor="preferred_courier" className="font-semibold">
                          Courier Name <span className="text-red-600">*</span>
                        </Label>
                        <Input
                          id="preferred_courier"
                          type="text"
                          value={formData.preferred_courier}
                          onChange={(e) => handleChange('preferred_courier', e.target.value)}
                          className="rounded-sm mt-1"
                          placeholder="Your preferred courier for urgent orders"
                          required
                          data-testid="courier-input"
                        />
                      </div>
                    )}
                    
                    {formData.dispatch_mode === 'local' && (
                      <div className="flex items-center">
                        <p className="text-sm text-green-700 bg-green-50 p-2 rounded border border-green-200">
                          Local dispatch - No transporter, carton packing, or LR required
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Remarks field - always available */}
                  <div className="mt-4">
                    <Label htmlFor="dispatch_remarks" className="font-semibold">
                      Dispatch Remarks <span className="text-gray-400">(Optional)</span>
                    </Label>
                    <Textarea
                      id="dispatch_remarks"
                      value={formData.dispatch_remarks}
                      onChange={(e) => handleChange('dispatch_remarks', e.target.value)}
                      className="rounded-sm mt-1"
                      rows={2}
                      placeholder="E.g., Will collect from shop, Send to branch..."
                      data-testid="dispatch-remarks-input"
                    />
                  </div>
                </div>

                {/* Private Marka - Optional */}
                <div>
                  <Label htmlFor="private_marka" className="font-semibold">
                    Private Marka <span className="text-gray-400">(Optional)</span>
                  </Label>
                  <Input
                    id="private_marka"
                    type="text"
                    value={formData.private_marka}
                    onChange={(e) => handleChange('private_marka', e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="Enter private marka"
                    data-testid="private-marka-input"
                  />
                </div>
              </div>

              {/* Save Info Button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveInfo}
                disabled={savingInfo}
                className="w-full mt-4 border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="save-info-button"
              >
                {savingInfo ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Information for Future Orders'
                )}
              </Button>

              {/* Place Order Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white rounded-sm h-12 uppercase font-semibold"
                data-testid="place-order-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Place Order'
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-border rounded-sm p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="text-xl font-heading font-bold mb-4">Order Summary</h2>

              {/* Show discount info */}
              {user?.discount_percentage > 0 && !cart.some(item => item.has_special_price) && (
                <div className="bg-green-50 border border-green-200 rounded-sm p-3 mb-4">
                  <p className="text-green-700 font-medium text-sm">
                    🎉 You have {user.discount_percentage}% discount applied!
                  </p>
                </div>
              )}
              
              {/* Show special price info */}
              {cart.some(item => item.has_special_price) && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4">
                  <p className="text-amber-700 font-medium text-sm">
                    ⭐ Some items have your negotiated special prices!
                  </p>
                </div>
              )}

              <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
                {cart.map((item) => {
                  // Use the discounted_price from cart (which includes special price if applicable)
                  const effectivePrice = item.discounted_price || item.mrp;
                  const itemTotal = effectivePrice * item.quantity;
                  
                  return (
                    <div
                      key={item.jp_part_no}
                      className="flex justify-between text-sm pb-3 border-b border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {item.part_name}
                          {item.has_special_price && (
                            <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Special</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">
                          {item.new_jp_part_no || item.jp_part_no}
                          {item.new_jp_part_no && item.new_jp_part_no !== item.jp_part_no && (
                            <span className="text-[10px] text-gray-400 ml-1">({item.jp_part_no})</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                        {effectivePrice < item.mrp && (
                          <p className="text-xs text-gray-400">
                            MRP: ₹{item.mrp.toFixed(2)} → ₹{effectivePrice.toFixed(2)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${item.has_special_price ? 'text-amber-600' : 'text-gray-900'}`}>
                          ₹{itemTotal.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-300 pt-4 space-y-2">
                {totalDiscounted < totalMRP && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total MRP:</span>
                      <span className="text-gray-600">₹{totalMRP.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="font-bold text-lg text-gray-900">Final Amount</span>
                  <span className={`font-bold text-2xl ${hasSpecialPrices ? 'text-amber-600' : 'text-green-600'}`} data-testid="checkout-total">
                    ₹{totalDiscounted.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  You will receive an order confirmation email after placing your order.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
